<template>
  <div
    :class="[{ 'product-options__seperator-wrapper': isOptionSeperator, 'is-wrapped': isWrapped }]"
    ref="seperatorWrapper"
  >
    <!-- Handle Edition Type Groups -->
    <template v-for="(groupItem, groupIndex) in group" :key="groupIndex">
      <!-- Edition Type Group -->
      <div v-if="groupItem.editionType" class="product-options__edition-wrapper">
        <div class="product-options__edition-inner-wrapper">
          <div v-if="groupItem.editionType" class="product-options__edition-title">
            {{ groupItem.editionType }}
          </div>

          <div class="product-options__edition-separator-wrapper">
            <!-- Price Groups within Edition Type -->
            <div v-for="(priceGroup, priceIndex) in groupItem.priceGroups" :key="`${groupIndex}-${priceIndex}`" class="product-options__seperator">
              <div class="product-options__color-inner-wrapper">
                <span v-if="isOptionSeperator && showPrice" class="product-options__option-price">
                  {{ formatPrice(priceGroup.price) }}
                </span>
                <div class="product-options__color-options">
                  <div class="product-options__color-option-wrapper" v-for="(optionValue, index) in priceGroup.options" :key="index">
                    <button
                      :class="[
                        optionValue.image ? 'product-options__item' : 'product-options__item-swatch-missing',
                        {
                          'product-options__item-swatch': isImageTypeSwatch && optionValue.image,
                          'product-options__item-image': !isImageTypeSwatch && optionValue.image,
                          selected: optionValue.value === selectedOptions[optionName],
                          'is--onesize': optionValue.value.toLowerCase() === 'one size',
                        },
                      ]"
                      data-stio-gtm-id="PDP: Color Option"
                      data-color-swatch
                      :data-available="optionValue.available"
                      :disabled="hideUnavailableOption ? !optionValue.available : false"
                      @click="() => updateSelection(optionName, optionValue.value, optionValue)"
                      :aria-label="`option: ${optionName} ${getModifiedOptionName(optionName, optionValue.value)}`"
                      :aria-pressed="optionValue.value === selectedOptions[optionName]"
                      :aria-disabled="!optionValue.available"
                    >
                      <ImageField
                        v-if="optionValue.image"
                        :key="optionValue.image"
                        :obj="{ url: optionValue.image, alt: optionValue.value, width: 288, height: 356 }"
                      />
                      <span v-else class="product-options__item-name">
                        {{ getModifiedOptionName(optionName, optionValue.value) }}
                      </span>

                      <!-- Strike Line Handling if not available -->
                      <span
                        v-if="!optionValue.available"
                        class="strike-line"
                        :style="{
                          width: calculateDiagonal(index) + 'px',
                          transform: 'translate(-50%, -50%) rotate(' + calculateAngle(index) + 'deg)',
                        }"
                      ></span>
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        <div v-if="groupIndex !== group.length - 1" class="product-options__edition-vertical-line"></div>
      </div>

      <!-- Regular Price Group (no edition type) -->
      <div v-else class="product-options__seperator">
        <div class="product-options__color-inner-wrapper">
          <span v-if="isOptionSeperator && showPrice" class="product-options__option-price">{{ formatPrice(groupItem.price) }}</span>
          <div class="product-options__color-options">
            <div class="product-options__color-option-wrapper" v-for="(optionValue, index) in groupItem.options" :key="index">
              <button
                :class="[
                  optionValue.image ? 'product-options__item' : 'product-options__item-swatch-missing',
                  {
                    'product-options__item-swatch': isImageTypeSwatch && optionValue.image,
                    'product-options__item-image': !isImageTypeSwatch && optionValue.image,
                    selected: optionValue.value === selectedOptions[optionName],
                    'is--onesize': optionValue.value.toLowerCase() === 'one size',
                  },
                ]"
                data-stio-gtm-id="PDP: Color Option"
                data-color-swatch
                :data-available="optionValue.available"
                :disabled="hideUnavailableOption ? !optionValue.available : false"
                @click="() => updateSelection(optionName, optionValue.value, optionValue)"
                :aria-label="`option: ${optionName} ${getModifiedOptionName(optionName, optionValue.value)}`"
                :aria-pressed="optionValue.value === selectedOptions[optionName]"
                :aria-disabled="!optionValue.available"
              >
                <ImageField
                  v-if="optionValue.image"
                  :key="optionValue.image"
                  :obj="{ url: optionValue.image, alt: optionValue.value, width: 288, height: 356 }"
                />
                <span v-else class="product-options__item-name">
                  {{ getModifiedOptionName(optionName, optionValue.value) }}
                </span>

                <!-- Strike Line Handling if not available -->
                <span
                  v-if="!optionValue.available"
                  class="strike-line"
                  :style="{
                    width: calculateDiagonal(index) + 'px',
                    transform: 'translate(-50%, -50%) rotate(' + calculateAngle(index) + 'deg)',
                  }"
                ></span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="groupIndex !== group.length - 1" class="product-options__color-option-vertical-line"></div>
      </div>
    </template>
  </div>
</template>

<script setup>
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import ImageField from '../global/image.vue';

  const props = defineProps({
    group: Array,
    optionName: String,
    selectedOptions: Object,
    hideUnavailableOption: Boolean,
    isOptionSeperator: Boolean,
    updateSelection: Function,
    formatPrice: Function,
    getModifiedOptionName: Function,
    isImageTypeSwatch: Boolean,
  });

  const seperatorWrapper = ref(null);
  const isWrapped = ref(false);
  
  onMounted(() => {
    checkWrap();
    window.addEventListener('resize', checkWrap);
  });

  const calculateDiagonal = (index) => {
    // Placeholder for diagonal calculation
    return 39;
  };

  const calculateAngle = (index) => {
    // Placeholder for angle calculation, passing static for now
    return -45;
  };

  const showPrice = computed(() => {
    if (!props.group || props.group.length === 0) return false;

    const allPrices = new Set();

    props.group.forEach(groupItem => {
      if (groupItem.editionType) {
        // Handle edition type groups - check all price groups within
        groupItem.priceGroups.forEach(priceGroup => {
          allPrices.add(priceGroup.price);
        });
      } else {
        // Handle regular price groups
        allPrices.add(groupItem.price);
      }
    });

    // Return true if there are different prices (more than one unique price)
    return allPrices.size > 1;
  });

  const checkWrap = () => {
    if (seperatorWrapper.value) {
      const el = seperatorWrapper.value;
      isWrapped.value = el.scrollHeight > el.clientHeight;
    }
  };

  onUnmounted(() => {
    window.removeEventListener('resize', checkWrap);
  });
</script>
